/** Zapytania i mutacje modułu `documents` (TanStack Query v5). */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Query } from '@/lib/api'
import { ApiError, api, authApi, tokenStore } from '@/lib/api'
import type { Paginated } from '@/lib/types'
import type {
  DocumentDetail,
  DocumentRow,
  InboxStats,
  Lawyer,
  RoutePayload,
  RoutingStage,
  VerifyPayload,
} from './types'

export const documentsKeys = {
  all: ['documents'] as const,
  list: (params: Query) => ['documents', 'list', params] as const,
  detail: (id: string | number) => ['documents', 'detail', String(id)] as const,
  pending: (limit: number) => ['documents', 'pending', limit] as const,
  stats: ['documents', 'stats'] as const,
  board: (stage: RoutingStage) => ['documents', 'board', stage] as const,
  lawyers: ['documents', 'lawyers'] as const,
}

export interface DocumentsListParams {
  page: number
  size?: number
  status?: string
  source?: string
  stage?: RoutingStage
  case_id?: number
  q?: string
  direction?: 'asc' | 'desc'
}

export function useDocuments(params: DocumentsListParams) {
  const query: Query = { ...params }
  return useQuery({
    queryKey: documentsKeys.list(query),
    queryFn: () => api.get<Paginated<DocumentRow>>('/documents', { query }),
    placeholderData: (previous) => previous,
  })
}

export function useDocument(documentId: string | undefined) {
  return useQuery({
    queryKey: documentsKeys.detail(documentId ?? ''),
    queryFn: () => api.get<DocumentDetail>(`/documents/${documentId}`),
    enabled: Boolean(documentId),
    retry: false,
  })
}

export function usePendingDocuments(limit = 6) {
  return useQuery({
    queryKey: documentsKeys.pending(limit),
    queryFn: () => api.get<DocumentRow[]>('/documents/pending', { query: { limit } }),
    staleTime: 30_000,
  })
}

export function useInboxStats() {
  return useQuery({
    queryKey: documentsKeys.stats,
    queryFn: () => api.get<InboxStats>('/documents/inbox/stats'),
    staleTime: 30_000,
  })
}

export function useBoardColumn(stage: RoutingStage) {
  return useQuery({
    queryKey: documentsKeys.board(stage),
    queryFn: () =>
      api.get<Paginated<DocumentRow>>('/documents', { query: { stage, size: 100 } }),
    staleTime: 15_000,
  })
}

/** Prawnicy do dekretacji — endpoint modułu `cases` (CONTRACTS.md §4c). */
export function useLawyers(enabled = true) {
  return useQuery({
    queryKey: documentsKeys.lawyers,
    queryFn: () => api.get<Lawyer[]>('/cases/lawyers'),
    staleTime: 5 * 60_000,
    enabled,
  })
}

/* ------------------------------------------------------------- mutacje */

function useInvalidate() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: documentsKeys.all })
  }
}

export function useUploadDocument() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ file, source }: { file: File; source: string }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', source)
      return api.upload<DocumentDetail>('/documents/upload', formData)
    },
    onSuccess: invalidate,
  })
}

export function useVerifyDocument(documentId: string | number) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (payload: VerifyPayload) =>
      api.post<DocumentDetail>(`/documents/${documentId}/verify`, payload),
    onSuccess: invalidate,
  })
}

export function useToDecretation(documentId: string | number) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: () => api.post<DocumentDetail>(`/documents/${documentId}/to-decretation`, {}),
    onSuccess: invalidate,
  })
}

/**
 * „Ponów" — dokument z błędem przetwarzania (albo odrzucony) wraca do kolejki AI.
 * `POST /api/documents/{id}/retry` (uprawnienie `documents.verify`).
 */
export function useRetryDocument() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (documentId: number | string) =>
      api.post<DocumentDetail>(`/documents/${documentId}/retry`, {}),
    onSuccess: invalidate,
  })
}

export function useRouteDocument() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, ...payload }: RoutePayload & { id: number }) =>
      api.post<DocumentDetail>(`/documents/${id}/route`, payload),
    onSuccess: invalidate,
  })
}

/* --------------------------------------------------------------- plik */

/**
 * Pobiera plik dokumentu jako blob URL (react-pdf nie zna naszego tokenu).
 * Na 401 jednorazowo odświeża token — tak jak klient `api`.
 */
export function useDocumentFile(documentId: string | number | undefined): {
  url: string | null
  mime: string | null
  error: string | null
} {
  const [url, setUrl] = useState<string | null>(null)
  const [mime, setMime] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!documentId) return
    let objectUrl: string | null = null
    let cancelled = false

    const load = async () => {
      setError(null)
      const path = `/api/documents/${documentId}/file`
      const fetchOnce = async (token: string | null) =>
        fetch(path, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: 'include',
        })

      try {
        let response = await fetchOnce(tokenStore.get())
        if (response.status === 401) {
          const token = await authApi.refresh()
          if (token) response = await fetchOnce(token)
        }
        if (!response.ok) {
          throw new ApiError(response.status, 'Nie udało się pobrać pliku', null, path)
        }
        const blob = await response.blob()
        if (cancelled) return
        // Typ bierzemy z nagłówka (blob.type bywa pusty), bez parametrów po średniku —
        // podgląd wybiera na jego podstawie renderer: pdf.js kontra <img>.
        const contentType = (response.headers.get('content-type') ?? blob.type ?? '')
          .split(';')[0]
          .trim()
          .toLowerCase()
        objectUrl = URL.createObjectURL(blob)
        setMime(contentType || null)
        setUrl(objectUrl)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.userMessage : 'Nie udało się pobrać pliku.')
        }
      }
    }

    void load()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      setUrl(null)
      setMime(null)
    }
  }, [documentId])

  return { url, mime, error }
}
